"use client"

import type React from "react"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { useAuthStore, DEMO_MODE } from "@/lib/store"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Activity, AlertCircle, ArrowLeft, KeyRound, Lock, Mail, ShieldCheck, User } from "lucide-react"

const DEMO_EMAIL = "demo@totalcontrolsystem.com"
const DEMO_PASSWORD = ""

export function LoginForm() {
  const [username, setUsername] = useState(DEMO_MODE ? DEMO_EMAIL : "")
  const [password, setPassword] = useState(DEMO_MODE ? DEMO_PASSWORD : "")
  const [error, setError] = useState("")
  const [info, setInfo] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [mode, setMode] = useState<"login" | "request" | "confirm">("login")
  const [resetUsername, setResetUsername] = useState("")
  const [resetCode, setResetCode] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmNewPassword, setConfirmNewPassword] = useState("")
  const login = useAuthStore((state) => state.login)
  const router = useRouter()

  const resetMessages = () => {
    setError("")
    setInfo("")
  }

  const isSecureContext = () => {
    if (typeof window === "undefined") return true
    const protocol = window.location.protocol
    const host = window.location.hostname
    return protocol === "https:" || host === "localhost" || host === "127.0.0.1"
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    resetMessages()
    if (!DEMO_MODE && !isSecureContext()) {
      setIsLoading(false)
      setError("Conéctate por HTTPS o red local segura antes de enviar credenciales.")
      return
    }
    setIsLoading(true)

    try {
      const success = await login(DEMO_MODE ? DEMO_EMAIL : username, DEMO_MODE ? DEMO_PASSWORD : password)
      if (success) {
        router.push("/dashboard")
      } else {
        setError("Credenciales inválidas. Verifica tu usuario y contraseña.")
      }
    } catch {
      setError("Error de conexión. Intenta de nuevo.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault()
    resetMessages()
    if (!resetUsername.trim()) {
      setError("Ingresa tu usuario o correo registrado.")
      return
    }
    if (!isSecureContext()) {
      setError("Conéctate por HTTPS o red local segura antes de enviar credenciales.")
      return
    }
    setIsLoading(true)

    try {
      const res = await fetch("/auth/request-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: resetUsername.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "No se pudo enviar el código de verificación.")
      }
      setInfo("Enviamos un código de verificación al correo asociado.")
      setMode("confirm")
    } catch (err) {
      const message = err instanceof Error ? err.message : "No se pudo enviar el código de verificación."
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault()
    resetMessages()
    if (!resetUsername.trim()) {
      setError("Solicita un código con tu usuario registrado.")
      return
    }
    if (!resetCode.trim()) {
      setError("Ingresa el código de verificación enviado.")
      return
    }
    if (!newPassword) {
      setError("Ingresa una nueva contraseña.")
      return
    }
    if (newPassword.length < 8) {
      setError("Usa al menos 8 caracteres con mayúsculas, minúsculas, dígitos y símbolos.")
      return
    }
    const strongPassword = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/
    if (!strongPassword.test(newPassword)) {
      setError("Usa al menos 8 caracteres con mayúsculas, minúsculas, dígitos y símbolos.")
      return
    }
    if (newPassword !== confirmNewPassword) {
      setError("Las contraseñas no coinciden.")
      return
    }

    setIsLoading(true)

    try {
      const res = await fetch("/auth/confirm-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: resetUsername.trim(), code: resetCode.trim(), newPassword }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "No se pudo actualizar la contraseña.")
      }
      setInfo("Contraseña actualizada. Inicia sesión con la nueva clave.")
      setPassword("")
      setNewPassword("")
      setConfirmNewPassword("")
      setResetCode("")
      setUsername(resetUsername.trim())
      setMode("login")
    } catch (err) {
      const message = err instanceof Error ? err.message : "No se pudo actualizar la contraseña."
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const goToLogin = () => {
    resetMessages()
    setMode("login")
  }

  const goToRequest = () => {
    resetMessages()
    setMode("request")
    if (username) setResetUsername(username)
  }

  const renderLogin = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {info && (
        <Alert className="border-primary/50 bg-primary/5">
          <ShieldCheck className="h-4 w-4" />
          <AlertDescription>{info}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="username" className="text-foreground">
          Nombre de Usuario
        </Label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="username"
            type="text"
            placeholder={DEMO_MODE ? DEMO_EMAIL : "admin"}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="pl-10 bg-input border-border"
            required
            disabled={DEMO_MODE}
            readOnly={DEMO_MODE}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password" className="text-foreground">
          Contraseña
        </Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="password"
            type="password"
            placeholder={DEMO_MODE ? "Modo demo: sin contraseña" : "••••••••"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10 bg-input border-border"
            required={!DEMO_MODE}
            disabled={DEMO_MODE}
            readOnly={DEMO_MODE}
          />
        </div>
      </div>

      <Button
        type="submit"
        className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
        disabled={isLoading}
      >
        {isLoading ? "Iniciando sesión..." : DEMO_MODE ? "Entrar en modo demo" : "Iniciar Sesión"}
      </Button>

      <Button type="button" variant="link" className="w-full text-sm" onClick={goToRequest} disabled={DEMO_MODE}>
        ¿Olvidaste tu contraseña?
      </Button>
    </form>
  )

  const renderRequestReset = () => (
    <form onSubmit={handleRequestReset} className="space-y-4">
      {error && (
        <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {info && (
        <Alert className="border-primary/50 bg-primary/5">
          <ShieldCheck className="h-4 w-4" />
          <AlertDescription>{info}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="reset-username" className="text-foreground">
          Usuario o correo registrado
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="reset-username"
            type="text"
            placeholder="usuario@dominio.com"
            value={resetUsername}
            onChange={(e) => setResetUsername(e.target.value)}
            className="pl-10 bg-input border-border"
            required
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Enviaremos un código de verificación al correo asociado para confirmar tu identidad.
        </p>
      </div>

      <Button
        type="submit"
        className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
        disabled={isLoading}
      >
        {isLoading ? "Enviando código..." : "Enviar código seguro"}
      </Button>

      <Button type="button" variant="ghost" className="w-full" onClick={goToLogin}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Volver a iniciar sesión
      </Button>
    </form>
  )

  const renderConfirmReset = () => (
    <form onSubmit={handleConfirmReset} className="space-y-4">
      {error && (
        <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {info && (
        <Alert className="border-primary/50 bg-primary/5">
          <ShieldCheck className="h-4 w-4" />
          <AlertDescription>{info}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="reset-username-confirm" className="text-foreground">
          Usuario
        </Label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="reset-username-confirm"
            type="text"
            value={resetUsername}
            onChange={(e) => setResetUsername(e.target.value)}
            className="pl-10 bg-input border-border"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="reset-code" className="text-foreground">
          Código de verificación
        </Label>
        <div className="relative">
          <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="reset-code"
            type="text"
            placeholder="123456"
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            className="pl-10 bg-input border-border tracking-[0.2em]"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="new-password" className="text-foreground">
          Nueva contraseña
        </Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="new-password"
            type="password"
            placeholder="••••••••"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="pl-10 bg-input border-border"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirm-password" className="text-foreground">
          Confirmar contraseña
        </Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="confirm-password"
            type="password"
            placeholder="••••••••"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            className="pl-10 bg-input border-border"
            required
          />
        </div>
        <p className="text-xs text-muted-foreground">
          La contraseña debe tener al menos 8 caracteres e incluir mayúsculas, minúsculas, dígitos y símbolos.
        </p>
      </div>

      <Button
        type="submit"
        className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
        disabled={isLoading}
      >
        {isLoading ? "Actualizando..." : "Confirmar cambio"}
      </Button>

      <div className="flex flex-col gap-2">
        <Button type="button" variant="ghost" className="w-full" onClick={goToRequest}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Reenviar código
        </Button>
        <Button type="button" variant="link" className="w-full text-sm" onClick={goToLogin}>
          Volver a iniciar sesión
        </Button>
      </div>
    </form>
  )

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md relative border-panel-border bg-card/80 backdrop-blur-sm">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center border border-primary/20">
            <Activity className="w-8 h-8 text-primary" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-foreground">Total Control System</CardTitle>
            <CardDescription className="text-muted-foreground mt-2">
              {DEMO_MODE ? "Modo demo de solo visualización" : "Plataforma de Monitoreo en Tiempo Real"}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          {mode === "login" && renderLogin()}
          {mode === "request" && renderRequestReset()}
          {mode === "confirm" && renderConfirmReset()}
        </CardContent>
      </Card>
    </div>
  )
}
